import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogContent, MatDialogTitle } from "@angular/material/dialog";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import type { ApiEndpointData } from "../../../../model/block.types";

export interface UrlSnippetDialogData {
  endpointData: ApiEndpointData;
}

/**
 * Dialog that shows the endpoint URL and ready-to-use code snippets (cURL, Python).
 * Opened by the show_url_snippet action from ContentActionBarComponent.
 */
@Component({
  selector: "app-url-snippet-dialog",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogClose, MatDialogContent, MatDialogTitle, MatButton, MatIconButton, MatIcon, MatTab, MatTabGroup],
  templateUrl: "./url-snippet-dialog.component.html",
  styleUrl: "./url-snippet-dialog.component.scss",
})
export class UrlSnippetDialogComponent {
  protected readonly dialogData = inject<UrlSnippetDialogData>(MAT_DIALOG_DATA);

  /** Tracks which snippet key was last copied (for button feedback). */
  protected readonly copied = signal<"url" | "curl" | "python" | null>(null);

  protected readonly curlSnippet = computed(() => {
    const { method, url } = this.dialogData.endpointData;
    return `curl -X ${method.toUpperCase()} \\\n  "${url}"`;
  });

  protected readonly pythonSnippet = computed(() => {
    const { method, url } = this.dialogData.endpointData;
    return [
      "import requests",
      "",
      `response = requests.${method.toLowerCase()}("${url}")`,
      "response.raise_for_status()",
      "print(response.json())",
    ].join("\n");
  });

  protected async copy(text: string, key: "url" | "curl" | "python"): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.copied.set(key);
      setTimeout(() => this.copied.set(null), 2000);
    } catch (err) {
      console.error("UrlSnippetDialog: clipboard write failed:", err);
    }
  }
}
